import React, { useContext, useEffect, useState, useRef } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import {
  createProject,
  updateProject,
  summarizeProjectProductsForLog,
} from '../../clients/project_client';
import { getProductSelectListWithSuppliers } from '../../clients/product_client';
import { getUserSelectList } from '../../clients/user_client';
import { getSupplierSelectList } from '../../clients/supplier_client';
import globalSupplierSelectCache from '../../config_and_helpers/supplier_cache';
import { showToast } from '../../config_and_helpers/helpers';
import {
  PROJECT_STATUS_OPTIONS,
  PROJECT_PRODUCT_STATUS_OPTIONS,
} from '../../config_and_helpers/config';
import { PlusCircle, TrashFill } from 'react-bootstrap-icons';
import DropdownSelect from '../Generic/DropdownSelect';
import BudgetLineSelect from '../LabData/BudgetLineSelect';
import RequiredAsteriskComponent from '../Generic/RequiredAsteriskComponent';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import AdHocLinesEditor from './AdHocLinesEditor';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { logError, logInfo } from '../../config_and_helpers/logger';
import '../Glass/ProcureModalStyle.css';
import '../Glass/GlassStyle.css';
import {
  buildAdHocPayload,
  mapAdHocFromProjectProduct,
  validateAdHocPayload,
} from '../../utils/projectAdHocPayload';

/**
 * Represents a modal component for creating or updating a project.
 *
 * @param {function} onSuccessfulSubmit - The function to be called when the form submission is successful.
 * @param {object} projectObj - The object representing the project. If provided, the modal will be used to update an existing project.
 * @param {function} clearSearchValue - The function to be called to clear the search value.
 */
const ProjectFormModal = ({
  onSuccessfulSubmit,
  projectObj,
  clearSearchValue,
}) => {
  const { token, userDetails } = useContext(AppContext);
  const isViewer = useIsLabViewer();

  const toIntOrNull = value => {
    if (value == null || value === '') return null;
    const numeric = Number(value);
    return Number.isInteger(numeric) ? numeric : null;
  };

  const resolveOwnerId = rawOwner => {
    const selectedOwner = toIntOrNull(rawOwner);
    if (selectedOwner != null) return selectedOwner;

    const projectOwnerObjId = toIntOrNull(projectObj?.owner?.id);
    if (projectOwnerObjId != null) return projectOwnerObjId;

    const projectOwnerScalar = toIntOrNull(projectObj?.owner);
    if (projectOwnerScalar != null) return projectOwnerScalar;

    const projectOwnerIdField = toIntOrNull(projectObj?.owner_id);
    if (projectOwnerIdField != null) return projectOwnerIdField;

    return projectObj ? null : toIntOrNull(userDetails?.id);
  };

  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [productSelectList, setProductSelectList] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [userSelectList, setUserSelectList] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [supplierSelectList, setSupplierSelectList] = useState([]);
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(false);
  const hasFetchedProductsRef = useRef(false);
  const hasFetchedUsersRef = useRef(false);
  const hasFetchedSuppliersRef = useRef(false);

  // Reset product select list and fetch flag when modal closes
  useEffect(() => {
    if (!showModal) {
      setProductSelectList([]);
      setUserSelectList([]);
      hasFetchedProductsRef.current = false;
      hasFetchedUsersRef.current = false;
    }
  }, [showModal]);

  // Form state
  const [name, setName] = useState(projectObj?.name || '');
  const [description, setDescription] = useState(projectObj?.description || '');
  const [status, setStatus] = useState(projectObj?.status || 'PLANNING');
  const [expectedCompletionDate, setExpectedCompletionDate] = useState(
    projectObj?.expected_completion_date
      ? new Date(projectObj.expected_completion_date)
      : null
  );
  const [budgetLineId, setBudgetLineId] = useState(
    projectObj?.budget_line?.id ?? ''
  );
  const [owner, setOwner] = useState(resolveOwnerId(null));

  useEffect(() => {
    logInfo('ProjectFormModal', 'owner state changed', {
      ownerState: owner,
      ownerStateType: typeof owner,
    });
  }, [owner]);
  // Helper function to organize inventory products by supplier
  const organizeProductsBySupplier = (products, productSelectList = []) => {
    if (!products || products.length === 0) {
      return [];
    }

    const supplierMap = {};
    products
      .filter(
        pp =>
          pp.is_inventory_line !== false &&
          (pp.product?.id || pp.product_id)
      )
      .forEach(pp => {
      const productId = pp.product?.id || pp.product_id;
      // Find supplier info from product object or productSelectList
      let supplierId = null;
      let supplierName = 'No Supplier';
      
      if (pp.product?.supplier) {
        if (typeof pp.product.supplier === 'object') {
          supplierId = pp.product.supplier.id;
          supplierName = pp.product.supplier.name || 'Unknown Supplier';
        } else {
          supplierId = pp.product.supplier;
          supplierName = pp.product.supplier_name || 'Unknown Supplier';
        }
      } else if (productSelectList && productId) {
        // Try to find supplier from productSelectList
        const productOption = productSelectList.find(p => p.value === productId || p.value === Number(productId));
        if (productOption?.supplier) {
          supplierId = productOption.supplier.id;
          supplierName = productOption.supplier.name;
        }
      }
      
      const key = supplierId || 'no-supplier';
      if (!supplierMap[key]) {
        supplierMap[key] = {
          supplier_id: supplierId || '',
          supplier_name: supplierName,
          products: [],
        };
      }
      
      supplierMap[key].products.push({
        product_id: productId,
        quantity_needed: pp.quantity_needed || 1,
        status: pp.status || 'NEEDS_QUOTE',
        notes: pp.notes || '',
      });
    });

    return Object.values(supplierMap);
  };

  const [adHocLines, setAdHocLines] = useState([]);
  const [hasInitializedAdHoc, setHasInitializedAdHoc] = useState(false);

  // Initialize supplier groups from projectObj or empty
  const [supplierGroups, setSupplierGroups] = useState(() => {
    if (projectObj?.project_products) {
      // Need to fetch product details to get supplier info, so start with empty
      // Will be populated after products are fetched
      return [];
    }
    return [];
  });

  // Track if we've initialized supplier groups from projectObj
  const [hasInitializedGroups, setHasInitializedGroups] = useState(false);

  // Fetch supplier select list when modal opens
  useEffect(() => {
    if (showModal && !hasFetchedSuppliersRef.current && !isLoadingSuppliers) {
      hasFetchedSuppliersRef.current = true;
      setIsLoadingSuppliers(true);
      
      // Check cache first
      const cacheIsValid =
        globalSupplierSelectCache.data &&
        (!globalSupplierSelectCache.invalidatedAt ||
          !globalSupplierSelectCache.timestamp ||
          globalSupplierSelectCache.timestamp >
            globalSupplierSelectCache.invalidatedAt);

      if (cacheIsValid) {
        setSupplierSelectList(globalSupplierSelectCache.data);
        setIsLoadingSuppliers(false);
        return;
      }

      if (
        globalSupplierSelectCache.isFetching &&
        globalSupplierSelectCache.promise
      ) {
        globalSupplierSelectCache.promise.then(() => {
          if (globalSupplierSelectCache.data) {
            setSupplierSelectList(globalSupplierSelectCache.data);
          }
          setIsLoadingSuppliers(false);
        });
        return;
      }

      globalSupplierSelectCache.isFetching = true;
      globalSupplierSelectCache.promise = getSupplierSelectList(token, data => {
        globalSupplierSelectCache.data = data;
        globalSupplierSelectCache.timestamp = Date.now();
        globalSupplierSelectCache.isFetching = false;
        setSupplierSelectList(data);
        setIsLoadingSuppliers(false);
      });

      globalSupplierSelectCache.promise.then(() => {
        globalSupplierSelectCache.isFetching = false;
      }).catch(error => {
        logError('ProjectFormModal', 'getSupplierSelectList failed', error);
        setSupplierSelectList([]);
        setIsLoadingSuppliers(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, token]);

  // Fetch user select list when modal opens
  useEffect(() => {
    if (showModal && !hasFetchedUsersRef.current && !isLoadingUsers) {
      hasFetchedUsersRef.current = true;
      setIsLoadingUsers(true);
      getUserSelectList(token, data => {
        if (data && Array.isArray(data)) {
          setUserSelectList(data);
        } else {
          setUserSelectList([]);
        }
        setIsLoadingUsers(false);
      }).catch(error => {
        logError('ProjectFormModal', 'getUserSelectList failed', error);
        setUserSelectList([]);
        setIsLoadingUsers(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, token]);

  // Fetch all products with supplier information when modal opens (for filtering)
  useEffect(() => {
    if (showModal && productSelectList.length === 0 && !isLoadingProducts) {
      setIsLoadingProducts(true);
      getProductSelectListWithSuppliers(token, data => {
        if (data && Array.isArray(data)) {
          setProductSelectList(data);
        } else {
          setProductSelectList([]);
        }
        setIsLoadingProducts(false);
      }).catch(error => {
        logError('ProjectFormModal', 'getProductSelectListWithSuppliers failed', error);
        setProductSelectList([]);
        setIsLoadingProducts(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, token]);

  // Initialize supplier groups from projectObj when editing
  useEffect(() => {
    if (showModal && projectObj?.project_products && !hasInitializedGroups && productSelectList.length > 0) {
      const groups = organizeProductsBySupplier(projectObj.project_products, productSelectList);
      setSupplierGroups(groups);
      setHasInitializedGroups(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, projectObj, productSelectList]);

  useEffect(() => {
    if (showModal && projectObj?.project_products && !hasInitializedAdHoc) {
      const lines = projectObj.project_products
        .filter(
          pp =>
            pp.is_inventory_line === false ||
            (!pp.product?.id && !pp.product_id)
        )
        .map(mapAdHocFromProjectProduct);
      setAdHocLines(lines);
      setHasInitializedAdHoc(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, projectObj]);

  // Reset form when modal closes
  useEffect(() => {
    if (!showModal) {
      setName(projectObj?.name || '');
      setDescription(projectObj?.description || '');
      setStatus(projectObj?.status || 'PLANNING');
      setExpectedCompletionDate(
        projectObj?.expected_completion_date
          ? new Date(projectObj.expected_completion_date)
          : null
      );
      setBudgetLineId(projectObj?.budget_line?.id ?? '');
      setOwner(resolveOwnerId(null));
      setSupplierGroups([]);
      setAdHocLines([]);
      setHasInitializedGroups(false);
      setHasInitializedAdHoc(false);
      setProductSelectList([]);
      hasFetchedProductsRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, projectObj]);

  const handleClose = () => {
    setShowModal(false);
  };

  const handleShow = () => {
    setShowModal(true);
  };

  // Add a new supplier group
  const addSupplierGroup = () => {
    setSupplierGroups([
      ...supplierGroups,
      {
        supplier_id: '',
        supplier_name: '',
        products: [
          {
            product_id: '',
            quantity_needed: 1,
            status: 'NEEDS_QUOTE',
            notes: '',
          },
        ],
      },
    ]);
  };

  // Remove a supplier group
  const removeSupplierGroup = (groupIndex) => {
    setSupplierGroups(supplierGroups.filter((_, i) => i !== groupIndex));
  };

  // Update supplier for a group
  const updateSupplierGroup = (groupIndex, supplierId, supplierName) => {
    const updated = [...supplierGroups];
    updated[groupIndex] = {
      ...updated[groupIndex],
      supplier_id: supplierId,
      supplier_name: supplierName,
    };
    setSupplierGroups(updated);
    
    // Clear products when supplier changes
    if (supplierId) {
      updated[groupIndex].products = [{
        product_id: '',
        quantity_needed: 1,
        status: 'NEEDS_QUOTE',
        notes: '',
      }];
      setSupplierGroups(updated);
    }
  };

  // Get products filtered by supplier for a specific group
  const getProductsForSupplier = (supplierId) => {
    if (!supplierId || !productSelectList.length) {
      return [];
    }
    return productSelectList.filter(product => {
      const productSupplierId = product.supplier?.id;
      return productSupplierId === supplierId || productSupplierId === Number(supplierId);
    });
  };

  // Add a product to a supplier group
  const addProductToGroup = (groupIndex) => {
    const updated = [...supplierGroups];
    updated[groupIndex].products.push({
      product_id: '',
      quantity_needed: 1,
      status: 'NEEDS_QUOTE',
      notes: '',
    });
    setSupplierGroups(updated);
  };

  // Remove a product from a supplier group
  const removeProductFromGroup = (groupIndex, productIndex) => {
    const updated = [...supplierGroups];
    updated[groupIndex].products = updated[groupIndex].products.filter((_, i) => i !== productIndex);
    setSupplierGroups(updated);
  };

  // Update a product in a supplier group
  const updateProductInGroup = (groupIndex, productIndex, field, value) => {
    const updated = [...supplierGroups];
    updated[groupIndex].products[productIndex] = {
      ...updated[groupIndex].products[productIndex],
      [field]: value,
    };
    setSupplierGroups(updated);
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!name.trim()) {
      showToast('Project name is required', 'error', 'top-right', 3000);
      return;
    }

    // Flatten supplier groups to project_products array
    const flattenedProducts = supplierGroups
      .filter(group => group.supplier_id) // Only include groups with a supplier selected
      .flatMap(group =>
        group.products
          .filter(product => product.product_id) // Only include products with a product selected
          .map(product => ({
            product_id: parseInt(product.product_id),
            quantity_needed: parseInt(product.quantity_needed) || 1,
            status: product.status,
            notes: product.notes?.trim() || '',
          }))
      );

    const adHocPayload = buildAdHocPayload(adHocLines);

    const allLines = [...flattenedProducts, ...adHocPayload];
    if (allLines.length === 0) {
      showToast(
        'Add at least one inventory product or custom line',
        'error',
        'top-right',
        3000
      );
      return;
    }

    const adHocValidation = validateAdHocPayload(adHocPayload);
    if (!adHocValidation.valid) {
      showToast(
        adHocValidation.message,
        'error',
        'top-right',
        4000
      );
      return;
    }

    setIsSubmitting(true);

    const lineSummary = summarizeProjectProductsForLog(allLines);
    logInfo('ProjectFormModal', 'save project submit', {
      isUpdate: Boolean(projectObj?.id),
      projectId: projectObj?.id ?? null,
      ...lineSummary,
    });

    const resolvedOwner = resolveOwnerId(owner);
    logInfo('ProjectFormModal', 'owner resolution debug', {
      ownerRaw: owner,
      ownerResolved: resolvedOwner,
      projectOwnerObjectId: projectObj?.owner?.id ?? null,
      projectOwnerScalar: projectObj?.owner ?? null,
      projectOwnerIdField: projectObj?.owner_id ?? null,
    });

    const projectData = {
      name: name.trim(),
      description: description.trim(),
      status,
      budget_id: budgetLineId ? Number(budgetLineId) : null,
      expected_completion_date: expectedCompletionDate
        ? expectedCompletionDate.toISOString().split('T')[0]
        : null,
      owner: resolvedOwner,
      project_products: allLines,
    };

    try {
      let response;
      if (projectObj) {
        response = await updateProject(token, projectObj.id, projectData);
      } else {
        response = await createProject(token, projectData);
      }

      if (response && response.success) {
        if (response.toast) {
          response.toast();
        }
        handleClose();
        if (onSuccessfulSubmit) {
          onSuccessfulSubmit();
        }
        if (clearSearchValue) {
          clearSearchValue();
        }
      } else {
        const errorMessage = Array.isArray(response)
          ? response.join(', ')
          : response?.messages?.length
            ? response.messages.join(', ')
            : 'Failed to save project';
        showToast(errorMessage, 'error', 'top-right', 5000);
      }
    } catch (error) {
      logError('ProjectFormModal', 'handleSubmit save project failed', error);
      showToast('An error occurred while saving the project', 'error', 'top-right', 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isViewer) {
    return null;
  }

  return (
    <>
      <Button
        variant={projectObj ? 'outline-success' : 'success'}
        onClick={handleShow}
      >
        {projectObj ? 'Edit Project' : 'Create Project'}
      </Button>

      <Modal
        show={showModal}
        onHide={handleClose}
        backdrop="static"
        dialogClassName="procure-modal-glass procure-modal-glass-xl"
      >
        <Modal.Header closeButton className="procure-modal__header">
          <Modal.Title>
            {projectObj
              ? `Edit Project ${projectObj.name || `#${projectObj.id}`}`
              : 'Create New Project'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body className="procure-modal__body">
            <Form.Group className="mb-3">
              <Form.Label>
                Project Name <RequiredAsteriskComponent />
              </Form.Label>
              <Form.Control
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Enter project name"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Enter project description"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <div style={{ width: '100%' }}>
                    <DropdownSelect
                      optionsList={PROJECT_STATUS_OPTIONS}
                      label="Status"
                      selectedValue={
                        PROJECT_STATUS_OPTIONS.find(s => s.value === status) || null
                      }
                      setSelectedValue={option => setStatus(option?.value || 'PLANNING')}
                      placeholder="Select status"
                    />
                  </div>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Expected Completion Date</Form.Label>
                  <div style={{ width: '100%' }}>
                    <DatePicker
                      selected={expectedCompletionDate}
                      onChange={date => setExpectedCompletionDate(date)}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                      placeholderText="Select date"
                      isClearable
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Budget</Form.Label>
                  <BudgetLineSelect
                    token={token}
                    value={budgetLineId || null}
                    onChange={id => setBudgetLineId(id ?? '')}
                    clearable
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Owner</Form.Label>
                  <DropdownSelect
                    optionsList={userSelectList || []}
                    label="Owner"
                    selectedValue={
                      userSelectList.find(u => u.value === owner) || null
                    }
                    setSelectedValue={option => {
                      const nextOwner = option?.value ?? null;
                      logInfo('ProjectFormModal', 'owner selected from dropdown', {
                        ownerSelectedRaw: option,
                        ownerSelectedValue: nextOwner,
                        ownerSelectedType: typeof nextOwner,
                      });
                      setOwner(nextOwner);
                    }}
                    placeholder="Select owner (optional)"
                    disabled={isLoadingUsers}
                  />
                  <Form.Text className="text-muted">
                    Leave empty to assign yourself as owner
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label>Products by Supplier</Form.Label>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={addSupplierGroup}
                  type="button"
                >
                  <PlusCircle className="me-1" />
                  Add Supplier
                </Button>
              </div>

              {supplierGroups.length === 0 ? (
                <div className="text-muted text-center py-3">
                  No suppliers added. Click "Add Supplier" to add products for a supplier.
                </div>
              ) : (
                <div className="border rounded p-3" style={{ maxHeight: '500px', overflowY: 'auto', overflowX: 'hidden' }}>
                  {supplierGroups.map((group, groupIndex) => (
                    <div
                      key={groupIndex}
                      className="border rounded p-3 mb-3"
                      style={{ borderColor: '#dee2e6', backgroundColor: '#f8f9fa' }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div style={{ flex: 1, marginRight: '1rem' }}>
                          <Form.Label className="fw-bold">Supplier</Form.Label>
                          <DropdownSelect
                            optionsList={supplierSelectList || []}
                            label="Supplier"
                            selectedValue={
                              supplierSelectList.find(s => s.value === group.supplier_id) || null
                            }
                            setSelectedValue={option => {
                              const supplier = supplierSelectList.find(s => s.value === option?.value);
                              updateSupplierGroup(
                                groupIndex,
                                option?.value || '',
                                supplier?.label || ''
                              );
                            }}
                            placeholder="Select supplier"
                            disabled={isLoadingSuppliers}
                            disabledOptions={
                              // Disable suppliers that are already selected in other groups
                              supplierGroups
                                .filter((g, i) => i !== groupIndex && g.supplier_id)
                                .map(g => {
                                  const id = g.supplier_id;
                                  // Find matching option to get exact value format for comparison
                                  const matchingOption = supplierSelectList.find(
                                    s => s.value === id || s.value === Number(id) || String(s.value) === String(id)
                                  );
                                  // Use the option's value format if found, otherwise use the id as-is
                                  return matchingOption ? matchingOption.value : (Number(id) || id);
                                })
                            }
                          />
                        </div>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeSupplierGroup(groupIndex)}
                          type="button"
                          className="mt-4"
                        >
                          <TrashFill /> Remove Supplier
                        </Button>
                      </div>

                      {group.supplier_id && (
                        <>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <Form.Label className="mb-0">Products</Form.Label>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => addProductToGroup(groupIndex)}
                              type="button"
                            >
                              <PlusCircle className="me-1" />
                              Add Product
                            </Button>
                          </div>

                          {group.products.length === 0 ? (
                            <div className="text-muted text-center py-2">
                              No products added. Click "Add Product" to add one.
                            </div>
                          ) : (
                            group.products.map((product, productIndex) => (
                              <div
                                key={productIndex}
                                className="border rounded p-2 mb-2"
                                style={{ borderColor: '#dee2e6', backgroundColor: '#ffffff' }}
                              >
                                <Row className="g-2">
                                  <Col xs={12} md={5}>
                                    <Form.Label>Product</Form.Label>
                                    <div style={{ width: '100%' }}>
                                      <DropdownSelect
                                        optionsList={getProductsForSupplier(group.supplier_id) || []}
                                        label="Product"
                                        selectedValue={
                                          getProductsForSupplier(group.supplier_id).find(
                                            p => p.value === product.product_id
                                          ) || null
                                        }
                                        setSelectedValue={option =>
                                          updateProductInGroup(
                                            groupIndex,
                                            productIndex,
                                            'product_id',
                                            option?.value || ''
                                          )
                                        }
                                        placeholder={group.supplier_id ? "Select product" : "Select supplier first"}
                                        disabled={isLoadingProducts || !group.supplier_id}
                                      />
                                    </div>
                                  </Col>
                                  <Col xs={6} md={2}>
                                    <Form.Label>Quantity</Form.Label>
                                    <Form.Control
                                      type="number"
                                      min="1"
                                      value={product.quantity_needed}
                                      onChange={e =>
                                        updateProductInGroup(
                                          groupIndex,
                                          productIndex,
                                          'quantity_needed',
                                          parseInt(e.target.value) || 1
                                        )
                                      }
                                    />
                                  </Col>
                                  <Col xs={6} md={3}>
                                    <Form.Label>Status</Form.Label>
                                    <div style={{ width: '100%' }}>
                                      <DropdownSelect
                                        optionsList={PROJECT_PRODUCT_STATUS_OPTIONS}
                                        label="Status"
                                        selectedValue={
                                          PROJECT_PRODUCT_STATUS_OPTIONS.find(
                                            s => s.value === product.status
                                          ) || null
                                        }
                                        setSelectedValue={option =>
                                          updateProductInGroup(
                                            groupIndex,
                                            productIndex,
                                            'status',
                                            option?.value || 'NEEDS_QUOTE'
                                          )
                                        }
                                        placeholder="Select status"
                                      />
                                    </div>
                                  </Col>
                                  <Col xs={12} md={2} className="d-flex align-items-end">
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => removeProductFromGroup(groupIndex, productIndex)}
                                      type="button"
                                      className="w-100"
                                    >
                                      <TrashFill /> Remove
                                    </Button>
                                  </Col>
                                  <Col xs={12}>
                                    <Form.Label>Notes</Form.Label>
                                    <Form.Control
                                      type="text"
                                      value={product.notes || ''}
                                      onChange={e =>
                                        updateProductInGroup(
                                          groupIndex,
                                          productIndex,
                                          'notes',
                                          e.target.value
                                        )
                                      }
                                      placeholder="Optional notes"
                                    />
                                  </Col>
                                </Row>
                              </div>
                            ))
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Form.Group>

            <AdHocLinesEditor
              lines={adHocLines}
              onLinesChange={setAdHocLines}
              supplierSelectList={supplierSelectList || []}
              isLoadingSuppliers={isLoadingSuppliers}
              showStatus
            />
          </Modal.Body>
          <Modal.Footer className="procure-modal__footer">
            <Button
              variant="secondary"
              className="procure-modal__close-btn"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="glass-primary-btn"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : projectObj ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default ProjectFormModal;

